import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlayCircle as PlayCircleIcon,
  Add as AddIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

const Admin = () => {
  const history = useHistory();
  const [dramas, setDramas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDrama, setSelectedDrama] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    poster: null,
    posterPreview: null
  });

  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    poster: null,
    posterPreview: null
  });

  useEffect(() => {
    fetchDramas();
  }, []);

  const fetchDramas = async () => {
    try {
      const response = await fetch('/api/dramas');
      const data = await response.json();
      if (data.success) {
        setDramas(data.data);
      }
    } catch (error) {
      console.error('获取剧集列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          poster: file,
          posterPreview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditFormData({
          ...editFormData,
          poster: file,
          posterPreview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const response = await fetch('/api/upload-drama', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          poster: formData.posterPreview
        })
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({
          open: true,
          message: '剧集上传成功!',
          severity: 'success'
        });
        setOpenDialog(false);
        resetForm();
        fetchDramas();
      } else {
        throw new Error(result.message || '上传失败');
      }
    } catch (error) {
      console.error('上传剧集失败:', error);
      setSnackbar({
        open: true,
        message: error.message || '上传失败',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditing(true);

    try {
      const response = await fetch(`/api/dramas/${selectedDrama.name}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editFormData.name,
          description: editFormData.description
        })
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({
          open: true,
          message: '剧集编辑成功!',
          severity: 'success'
        });
        setOpenEditDialog(false);
        fetchDramas();
      } else {
        throw new Error(result.message || '编辑失败');
      }
    } catch (error) {
      console.error('编辑剧集失败:', error);
      setSnackbar({
        open: true,
        message: error.message || '编辑失败',
        severity: 'error'
      });
    } finally {
      setEditing(false);
    }
  };

  const handleDeleteDrama = async (dramaId) => {
    if (window.confirm('确定要删除这个剧集吗？')) {
      try {
        const response = await fetch(`/api/dramas/${dramaId}`, {
          method: 'DELETE'
        });
        
        const result = await response.json();
        
        if (result.success) {
          setSnackbar({
            open: true,
            message: '剧集删除成功!',
            severity: 'success'
          });
          fetchDramas();
        } else {
          throw new Error(result.message || '删除失败');
        }
      } catch (error) {
        console.error('删除剧集失败:', error);
        setSnackbar({
          open: true,
          message: error.message || '删除失败',
          severity: 'error'
        });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      poster: null,
      posterPreview: null
    });
  };

  const resetEditForm = () => {
    setEditFormData({
      name: '',
      description: '',
      poster: null,
      posterPreview: null
    });
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleOpenEditDialog = (drama) => {
    console.log('编辑按钮被点击:', drama);
    setSelectedDrama(drama);
    setEditFormData({
      name: drama.name,
      description: drama.description,
      poster: null,
      posterPreview: drama.poster
    });
    setOpenEditDialog(true);
    console.log('编辑对话框已打开');
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedDrama(null);
    resetEditForm();
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 打开上传剧集对话框
  const handleOpenUploadDialog = () => {
    setOpenUploadDialog(true);
  };

  // 关闭上传剧集对话框
  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
  };

  // 处理剧集文件选择
  const handleEpisodeFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadEpisodes(files);
    }
  };

  // 上传剧集
  const uploadEpisodes = async (files) => {
    if (!selectedDrama) return;
    
    const episodesData = [];
    for (let file of files) {
      await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          episodesData.push(e.target.result);
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }

    try {
      const response = await fetch('/api/upload-episodes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          drama: selectedDrama.name,
          episodes: episodesData
        })
      });

      const result = await response.json();

      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message,
          severity: 'success'
        });
        fetchDramas(); // 刷新剧集列表
      } else {
        throw new Error(result.message || '上传失败');
      }
    } catch (error) {
      console.error('上传剧集失败:', error);
      setSnackbar({
        open: true,
        message: error.message || '上传失败',
        severity: 'error'
      });
    }
  };

  // 打开剧集上传对话框
  const handleUploadEpisodes = () => {
    // 触发文件选择器
    const fileInput = document.querySelector('input[type="file"][accept="video/*"]');
    if (fileInput) {
      fileInput.click();
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 2, mb: 4, px: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress size={80} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4, px: 1 }}>
      <Typography variant="h3" component="h1" sx={{ mb: 4 }}>
        后台管理系统
      </Typography>

      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
            剧集管理
          </Typography>
          <Typography variant="body2" color="text.secondary">
            当前共有 {dramas.length} 个剧集
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            backgroundColor: '#1976d2',
            '&:hover': {
              backgroundColor: '#1565c0',
            },
          }}
        >
          上传新剧集
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>剧集信息</TableCell>
              <TableCell>海报</TableCell>
              <TableCell align="center">状态</TableCell>
              <TableCell align="center">总集数</TableCell>
              <TableCell align="center">播放量</TableCell>
              <TableCell align="center">点赞数</TableCell>
              <TableCell align="center">创建时间</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dramas.map((drama) => (
              <TableRow key={drama.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {drama.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {drama.description}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {drama.poster && (
                    <img
                      src={drama.poster}
                      alt={drama.name}
                      style={{
                        width: 80,
                        height: 120,
                        objectFit: 'cover',
                        borderRadius: 4
                      }}
                    />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={drama.status === 'active' ? '活跃' : '下架'}
                    color={drama.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">{drama.totalEpisodes || 0}</TableCell>
                <TableCell align="center">{drama.viewCount || 0}</TableCell>
                <TableCell align="center">{drama.likeCount || 0}</TableCell>
                <TableCell align="center">{drama.createTime}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<PlayCircleIcon />}
                      onClick={() => history.push(`/drama/${drama.id}`)}
                    >
                      预览
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => history.push(`/admin/edit/${drama.id}`)}
                      id={`edit-button-${drama.id}`}
                    >
                      编辑
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DeleteIcon />}
                      color="error"
                      onClick={() => handleDeleteDrama(drama.name)}
                    >
                      删除
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 上传对话框 */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>上传新剧集</DialogTitle>
        <DialogContent>
          <form onSubmit={handleFormSubmit}>
            <TextField
              fullWidth
              label="剧集名称"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="剧集描述"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              required
            />
            <Button
              variant="contained"
              component="label"
              fullWidth
              margin="normal"
              startIcon={<UploadIcon />}
            >
              上传海报
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageChange}
              />
            </Button>
            {formData.posterPreview && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <img
                  src={formData.posterPreview}
                  alt="预览"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    objectFit: 'contain',
                    borderRadius: 8
                  }}
                />
              </Box>
            )}
            <DialogActions sx={{ mt: 2 }}>
              <Button onClick={handleCloseDialog}>取消</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={uploading}
                startIcon={uploading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {uploading ? '上传中...' : '上传'}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/* 编辑对话框 */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>编辑剧集</DialogTitle>
        <DialogContent>
          <form onSubmit={handleEditSubmit}>
            <TextField
              fullWidth
              label="剧集名称"
              value={editFormData.name}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="剧集描述"
              multiline
              rows={3}
              value={editFormData.description}
              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              margin="normal"
              required
            />
            <Button
              variant="contained"
              component="label"
              fullWidth
              margin="normal"
              startIcon={<UploadIcon />}
            >
              更换海报
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleEditImageChange}
              />
            </Button>
            {editFormData.posterPreview && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <img
                  src={editFormData.posterPreview}
                  alt="预览"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    objectFit: 'contain',
                    borderRadius: 8
                  }}
                />
              </Box>
            )}
            
            {/* 上传剧集按钮 */}
            <Button
              variant="outlined"
              fullWidth
              margin="normal"
              startIcon={<UploadIcon />}
              onClick={handleOpenUploadDialog}
            >
              上传剧集
            </Button>
            
            {/* 已上传剧集列表 */}
            {selectedDrama && selectedDrama.episodes && selectedDrama.episodes.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  已上传剧集: {selectedDrama.episodes.length} 集
                </Typography>
                <Box sx={{ mt: 1, maxHeight: 200, overflowY: 'auto' }}>
                  {selectedDrama.episodes.map((episode, index) => (
                    <Chip
                      key={index}
                      label={`第${episode.episodeNumber}集`}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            <DialogActions sx={{ mt: 2 }}>
              <Button onClick={handleCloseEditDialog}>取消</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={editing}
                startIcon={editing ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {editing ? '保存中...' : '保存'}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/* 上传剧集模态框 */}
      <Dialog
        open={openUploadDialog}
        onClose={handleCloseUploadDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>上传剧集</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            请选择要上传的剧集文件。支持MP4格式，多个文件按选择顺序作为剧集顺序。
          </Typography>
          <input
            type="file"
            accept="video/mp4"
            multiple
            onChange={handleEpisodeFileChange}
            style={{ display: 'block', marginTop: '16px' }}
          />
          {selectedDrama && selectedDrama.episodes && selectedDrama.episodes.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" fontWeight="bold">
                已上传剧集: {selectedDrama.episodes.length} 集
              </Typography>
              <Box mt={1} maxHeight={200} overflowY="auto">
                {selectedDrama.episodes.map((episode, index) => (
                  <Chip
                    key={index}
                    label={`第${episode.episodeNumber}集`}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>取消</Button>
        </DialogActions>
      </Dialog>

      {/* 消息提示 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Admin;
